import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import X from 'react-feather/dist/icons/x';
import { CONSTANTS } from '../../../constants/constants';
import { TableFilterRow } from '@devexpress/dx-react-grid-material-ui';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import NRMultipleSelectCheckmarks from '../../NRMultipleSelectCheckmarks/NRMultipleSelectCheckmarks';
const { FILTER, CLEAR_FILTER_TOOLTIP } = CONSTANTS.TABLE;
const { TRUE, FALSE, ALL } = CONSTANTS.TABLE.FILTER_VALUES;

/**
 * Filter
 */
const FilterCellBase = ({ filter, onFilter, column, iconComponent, ...props }) => {
  return (
    <Box
      component="th"
      role="none"
      sx={theme => ({
        fontWeight: 'normal',
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(0),
        height: theme.spacing(6)
      })}
    >
      {/* we use align center or undefined (meaning left) so we translate to justifyContent: center || flex-start */}
      <Box
        component="div"
        sx={theme => ({
          display: 'flex',
          justifyContent: props?.tableColumn?.align || 'flex-start',
          height: '100%',
          ...(!!filter && {
            '& > #toggle-component': {
              color: alpha(theme.palette.secondary.dark, 0.54)
            }
          })
        })}
      >
        {!column.hideFilter && (
          <>
            <TextField
              id={`${column.name}-filter`}
              variant={'outlined'}
              color={'secondary'}
              inputProps={{
                style: { textAlign: 'start', padding: 3 },
                'aria-label': `Filter by ${column?.title?.value || column?.name || 'column'}`
              }}
              sx={theme => ({
                padding: theme.spacing(1),
                '& .MuiInputBase-adornedEnd': {
                  padding: theme.spacing(0, 1),
                  height: theme.spacing(4)
                },
                '& input[type=number]': {
                  '-moz-appearance': 'textfield'
                },
                '& input[type=number]::-webkit-outer-spin-button': {
                  '-webkit-appearance': 'none',
                  margin: 0
                },
                '& input[type=number]::-webkit-inner-spin-button': {
                  '-webkit-appearance': 'none',
                  margin: 0
                }
              })}
              placeholder={column.filterPlaceholder || FILTER}
              value={filter ? filter.value : ''}
              onChange={e =>
                onFilter(
                  e.target.value
                    ? { value: !!props.tableColumn.column.type ? Number(e.target.value) : e.target.value, operation: props.children[0].props.value }
                    : null
                )
              }
              {...(!!props.tableColumn.column.type && { type: props.tableColumn.column.type })}
              InputProps={{
                endAdornment: (
                  <>
                    {!!filter?.value && (
                      <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                        <Tooltip title={CLEAR_FILTER_TOOLTIP} placement="top">
                          <X size={16} onClick={() => onFilter(null)} />
                        </Tooltip>
                      </InputAdornment>
                    )}
                  </>
                ),
                startAdornment: <>{props.children[0]}</>
              }}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

const FilterSelectCell = props => {
  const [selectValue, setSelectValue] = useState(props?.filter?.value == null ? '' : props?.filter?.value);

  useEffect(() => {
    setSelectValue(props?.filter?.value == null ? '' : props?.filter?.value);
  }, [props.filter?.value]);

  const getFilterValue = val => {
    return val === 1
      ? { value: 1, operation: 'greaterThanOrEqual', columnName: props.column.name }
      : val === 0
      ? { value: 0, operation: 'equal', columnName: props.column.name }
      : null;
  };

  const handleSetSelectValue = e => {
    setSelectValue(e.target.value);
    props.onFilter(getFilterValue(e.target?.value));
  };

  return (
    <Box
      component="th"
      role="none"
      sx={theme => ({
        fontWeight: 'normal',
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(0),
        height: theme.spacing(6),
        maxWidth: props.column.filterMaxWidth
      })}
    >
      <Select
        style={{ width: 'calc(100% - 16px)', height: 32 }}
        variant={'outlined'}
        sx={{
          pt: 0,
          pb: 0,
          '& .MuiSelect-select:focus': {
            background: 'none'
          }
        }}
        SelectDisplayProps={{ style: { textAlign: 'left' } }}
        id={`select-filter-${props.column.name}`}
        inputProps={{
          'aria-label': `Filter by ${props?.column?.title?.value || props?.column?.name || 'column'}`,
          'aria-description': selectValue === '' ? 'No filter selected.' : `Current selected value: ${selectValue === 1 ? 'True' : 'False'}.`
        }}
        value={selectValue}
        onChange={handleSetSelectValue}
        color={'secondary'}
      >
        <MenuItem value={''}>{ALL}</MenuItem>
        <MenuItem value={1}>{props.column.filterValues?.true || TRUE}</MenuItem>
        <MenuItem value={0}>{props.column.filterValues?.false || FALSE}</MenuItem>
      </Select>
    </Box>
  );
};

const OnOffFilterCell = props => {
  const [selectValue, setSelectValue] = useState(props?.filter?.value || props?.filter?.value === false ? props?.filter?.value : null);

  useEffect(() => {
    setSelectValue(props?.filter?.value || props?.filter?.value === false ? props?.filter?.value : null);
  }, [props.filter?.value]);

  const getFilterValue = val => {
    return val === 'on'
      ? { value: 'on', operation: 'contains', columnName: props.column.name }
      : val === 'off'
      ? { value: 'off', operation: 'contains', columnName: props.column.name }
      : null;
  };

  const handleSetSelectValue = e => {
    setSelectValue(e.target.value);
    let value = getFilterValue(e.target?.value);
    props.onFilter(value);
  };

  return (
    <Box
      component="th"
      role="none"
      sx={theme => ({
        fontWeight: 'normal',
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(0),
        height: theme.spacing(6),
        maxWidth: props.column.filterMaxWidth
      })}
    >
      <Select
        style={{ width: 'calc(100% - 16px)', height: 32 }}
        variant={'outlined'}
        sx={{
          pt: 0,
          pb: 0,
          '& .MuiSelect-select:focus': {
            background: 'none'
          }
        }}
        SelectDisplayProps={{ style: { textAlign: 'left' } }}
        id={`select-filter-${props.column.name}`}
        inputProps={{
          'aria-label': `Filter by ${props?.column?.title?.value || props?.column?.name || 'column'}`,
          'aria-description': !selectValue ? 'No filter selected.' : `Current selected value: ${selectValue}.`
        }}
        value={selectValue}
        onChange={handleSetSelectValue}
        color={'secondary'}
      >
        <MenuItem value={null}>{ALL}</MenuItem>
        <MenuItem value={'on'}>{props.column.filterValues?.true || TRUE}</MenuItem>
        <MenuItem value={'off'}>{props.column.filterValues?.false || FALSE}</MenuItem>
      </Select>
    </Box>
  );
};
const BooleanBEFilterCell = props => {
  const ALL_VALUE = CONSTANTS.TABLE.FILTER_VALUES.ALL;
  const TRUE_VALUE = CONSTANTS.TABLE.FILTER_VALUES.TRUE;
  const FALSE_VALUE = CONSTANTS.TABLE.FILTER_VALUES.FALSE;
  const [selectValue, setSelectValue] = useState(props?.filter?.value || '');

  useEffect(() => {
    setSelectValue(props?.filter?.value || '');
  }, [props.filter?.value]);

  const getFilterValue = val => {
    if (!val) return null;
    return { value: val, operation: 'Equal', columnName: props.column.name };
  };

  const handleSetSelectValue = e => {
    setSelectValue(e.target.value);
    let value = getFilterValue(e.target?.value);
    props.onFilter(value);
  };

  return (
    <Box
      component="th"
      role="none"
      sx={theme => ({
        fontWeight: 'normal',
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(0),
        height: theme.spacing(6),
        maxWidth: props.column.filterMaxWidth
      })}
    >
      <Select
        style={{ width: 'calc(100% - 16px)', height: 32 }}
        variant={'outlined'}
        sx={{
          pt: 0,
          pb: 0,
          '& .MuiSelect-select:focus': {
            background: 'none'
          }
        }}
        SelectDisplayProps={{ style: { textAlign: 'left' } }}
        id={`select-filter-${props.column.name}`}
        inputProps={{
          'aria-label': `Filter by ${props?.column?.title?.value || props?.column?.name || 'column'}`,
          'aria-description': !selectValue ? 'No filter selected.' : `Current selected value: ${selectValue}.`
        }}
        value={selectValue}
        onChange={handleSetSelectValue}
        color={'secondary'}
      >
        <MenuItem value={''}>{ALL}</MenuItem>
        <MenuItem value={TRUE_VALUE}>{props.column.filterValues?.true || TRUE}</MenuItem>
        <MenuItem value={FALSE_VALUE}>{props.column.filterValues?.false || FALSE}</MenuItem>
      </Select>
    </Box>
  );
};

const MultiSelectFilterCell = props => {
  const [value, setValue] = useState(props?.filter?.value?.split('&') || []);

  useEffect(() => {
    setValue(props?.filter?.value?.split('&') || []);
  }, [props.filter?.value]);

  const getFilterValue = val => {
    if (!val?.length) return null;
    return { value: val.join('&'), operation: 'CONTAINS', columnName: props.column.name };
  };

  const handleChange = event => {
    const {
      target: { value }
    } = event;
    setValue(typeof value === 'string' ? value.split(',') : value);
    if (value) {
      let filter = getFilterValue(value);
      props.onFilter(filter);
    }
  };

  return (
    <Box
      component="th"
      role="none"
      sx={theme => ({
        fontWeight: 'normal',
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(0),
        height: theme.spacing(6),
        maxWidth: props.column.filterMaxWidth
      })}
    >
      <NRMultipleSelectCheckmarks
        selectId={`multiselect-filter-${props?.column?.name}`}
        values={props?.column?.filterValues}
        onChange={handleChange}
        value={props?.filter?.value?.split('&') || []}
        ariaLabel={`Filter by ${props?.column?.title?.value || props?.column?.name || 'column'}`}
        ariaDescription="This filter supports selecting multiple values."
      />
    </Box>
  );
};

const MultiSelectFilterCellFE = props => {
  const [value, setValue] = useState(props?.filter?.value?.split('&') || []);

  useEffect(() => {
    setValue(props?.filter?.value?.split('&') || []);
  }, [props.filter?.value]);

  const getFilterValue = val => {
    if (!val?.length) return null;
    return { value: val?.join('&'), operation: 'contains', columnName: props.column.name };
  };

  const handleChange = event => {
    const {
      target: { value }
    } = event;
    setValue(typeof value === 'string' ? value.split(',') : value);
    if (value) {
      let filter = getFilterValue(value);
      props.onFilter(filter);
    }
  };

  return (
    <Box
      component="th"
      role="none"
      sx={theme => ({
        fontWeight: 'normal',
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(0),
        height: theme.spacing(6),
        maxWidth: props.column.filterMaxWidth
      })}
    >
      <NRMultipleSelectCheckmarks
        selectId={`multiselect-filter-${props?.column?.name}`}
        values={props?.column?.filterValues}
        onChange={handleChange}
        value={value}
        ariaLabel={`Filter by ${props?.column?.title?.value || props?.column?.name || 'column'}`}
        ariaDescription="This filter supports selecting multiple values."
      />
    </Box>
  );
};

const MultiSelectFilterCellEnum = props => {
  const [value, setValue] = useState(props?.filter?.value?.split('&') || []);

  useEffect(() => {
    setValue(props?.filter?.value?.split('&') || []);
  }, [props.filter?.value]);

  const getFilterValue = val => {
    if (!val?.length) return null;
    return { value: val?.join('&'), operation: 'enum', columnName: props.column.name };
  };

  const handleChange = event => {
    const {
      target: { value }
    } = event;
    setValue(typeof value === 'string' ? value.split(',') : value);
    if (value) {
      let filter = getFilterValue(value);
      props.onFilter(filter);
    }
  };

  return (
    <Box
      component="th"
      role="none"
      sx={theme => ({
        fontWeight: 'normal',
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(0),
        height: theme.spacing(6),
        maxWidth: props.column.filterMaxWidth
      })}
    >
      <NRMultipleSelectCheckmarks
        selectId={`multiselect-filter-${props?.column?.name}`}
        values={props?.column?.filterValues}
        onChange={handleChange}
        value={value}
        ariaLabel={`Filter by ${props?.column?.title?.value || props?.column?.name || 'column'}`}
        ariaDescription="This filter supports selecting multiple values."
      />
    </Box>
  );
};

export const FilterCell = props => {
  if (props.column.name === 'actions') {
    return <TableFilterRow.Cell {...props}>&nbsp;</TableFilterRow.Cell>;
  } else if (props.column.type === 'booleanNumber') {
    return <FilterSelectCell {...props} />;
  } else if (props.column.type === 'onOff') {
    return <OnOffFilterCell {...props} />;
  } else if (props.column.type === 'booleanBE') {
    // "True" or "False" or ''
    return <BooleanBEFilterCell {...props} />;
  } else if (props.column.type === 'multiselect') {
    return <MultiSelectFilterCell {...props} />;
  } else if (props.column.type === 'multiselectFE') {
    return <MultiSelectFilterCellFE {...props} />;
  } else if (props.column.type === 'multiselectEnum') {
    return <MultiSelectFilterCellEnum {...props} />;
  } else if (props.column.filterOperations) {
    return <TableFilterRow.Cell {...props} filterOperations={props.column.filterOperations} />;
  }
  return <FilterCellBase {...props} />;
};
